import { useState, type Dispatch } from 'react';
import { QUANTITY_PRESETS } from '@shared/rfq';
import { fmt, useI18n } from '@/i18n';
import { ArtworkUploader } from '../artwork/ArtworkUploader';
import { round1 } from './geometry';
import { COVERAGES, INK_SETUPS, LIMITS, circumference, containersFor, getContainer, recommendedInk } from './model';
import { ProductThumb, inkWarnings, useClosureLabel, usePlacementWarnings } from './parts';
import { derive, type ProductAction, type ProductState } from './state';

interface StepProps {
  s: ProductState;
  dispatch: Dispatch<ProductAction>;
}

// ------------------------------------------------------------------ 1. container
export function StepContainer({ s, dispatch }: StepProps) {
  const { d } = useI18n();
  const f = d.cfg.families[s.product];
  const closure = useClosureLabel();
  const list = containersFor(s.product);
  return (
    <div className="step">
      <h2 className="step__title">{f.containerTitle}</h2>
      <p className="step__lead">{f.containerLead}</p>
      <p className="step__note mono">
        {fmt(d.cfg.container.limits, { d: LIMITS.diameterMm.join('–'), h: LIMITS.lengthMm.join('–') })}
      </p>
      <div className={`choice-grid ${list.length > 4 ? 'choice-grid--3' : 'choice-grid--2'}`} role="radiogroup" aria-label={f.containerTitle}>
        {list.map((c) => {
          const on = c.id === s.containerId;
          const mat = c.materials.includes(s.material) ? s.material : c.defaultMaterial;
          return (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={on}
              className={`choice choice--jar${on ? ' is-on' : ''}`}
              onClick={() => dispatch({ type: 'container', id: c.id })}
            >
              <span className="choice__visual">
                <ProductThumb c={c} material={mat} id={`thumb-${c.id}`} />
              </span>
              <span className="choice__name">{c.name}</span>
              <span className="choice__spec mono">
                Ø {c.diameterMm} × {c.heightMm} мм · {c.volumeMl} {d.common.ml}
              </span>
              <span className="choice__spec mono">
                {d.cfg.container.printArea}: {round1(circumference(c))} × {c.print.heightMm} мм
              </span>
              {closure(c) && <span className="choice__spec mono">{closure(c)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ 2. material
export function StepGlass({ s, dispatch }: StepProps) {
  const { d } = useI18n();
  const f = d.cfg.families[s.product];
  const c = getContainer(s.containerId);
  return (
    <div className="step">
      <h2 className="step__title">{f.materialTitle}</h2>
      <p className="step__lead">{f.materialLead}</p>
      <div className="choice-list" role="radiogroup" aria-label={f.materialTitle}>
        {c.materials.map((g) => {
          const on = g === s.material;
          const o = d.cfg.materials[g];
          return (
            <button key={g} type="button" role="radio" aria-checked={on} className={`choice choice--row${on ? ' is-on' : ''}`} onClick={() => dispatch({ type: 'material', material: g })}>
              <span className="choice__swatch">
                <ProductThumb c={c} material={g} id={`mat-${g}`} />
              </span>
              <span className="choice__body">
                <span className="choice__name">{o.label}</span>
                <span className="choice__desc">{o.d}</span>
                <span className="choice__spec mono">
                  {d.cfg.print.recommendedFor}: {d.cfg.print.inks[recommendedInk(g)].label}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ 3. upload
export function StepUpload({ s, dispatch }: StepProps) {
  const { d } = useI18n();
  const t = d.cfg.upload;
  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <p className="step__lead">{t.lead}</p>
      <ArtworkUploader
        artwork={s.artwork}
        status={s.upload.status}
        onArtwork={(artwork) => dispatch({ type: 'artwork', artwork })}
        onUpload={(status, id) => dispatch({ type: 'upload', status, id })}
        designHelp={s.designHelp}
        onDesignHelp={(value) => dispatch({ type: 'designHelp', value })}
      />
    </div>
  );
}

// ------------------------------------------------------------------ 4. editor controls
export function StepEditor({ s, dispatch }: StepProps) {
  const { d } = useI18n();
  const t = d.cfg.editor;
  const warn = usePlacementWarnings();
  const der = derive(s);
  const c = getContainer(s.containerId);
  const C = circumference(c);
  const p = s.placement;

  if (!s.artwork || !p) {
    return (
      <div className="step">
        <h2 className="step__title">{t.title}</h2>
        <p className="notice notice--info">{t.noArtwork}</p>
      </div>
    );
  }

  const setNum = (field: 'widthMm' | 'rotationDeg' | 'uMm' | 'vMm', raw: string) => {
    const v = parseFloat(raw.replace(',', '.'));
    if (!Number.isFinite(v)) return;
    if (field === 'widthMm') {
      const w = Math.max(4, Math.min(C * 1.5, v));
      dispatch({ type: 'placement', placement: { ...p, widthMm: w, heightMm: w / s.artwork!.aspect } });
    } else {
      dispatch({ type: 'placement', placement: { ...p, [field]: v } });
    }
  };

  const dpiText = der.dpi == null ? t.vectorDpi : `~${der.dpi} dpi`;

  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <p className="step__lead">{t.lead}</p>

      <div className="seg" role="tablist" aria-label={t.title}>
        <button type="button" role="tab" aria-selected={s.view === 'front'} className={s.view === 'front' ? 'is-on' : ''} onClick={() => dispatch({ type: 'view', view: 'front' })}>
          {t.viewFront}
        </button>
        <button type="button" role="tab" aria-selected={s.view === 'flat'} className={s.view === 'flat' ? 'is-on' : ''} onClick={() => dispatch({ type: 'view', view: 'flat' })}>
          {t.viewFlat}
        </button>
      </div>

      <div className="field">
        <label htmlFor="view-angle">
          {t.rotateView} <span className="mono muted">{Math.round(s.viewAngleDeg)}°</span>
        </label>
        <input id="view-angle" type="range" min="0" max="359" step="1" value={Math.round(s.viewAngleDeg)} onChange={(e) => dispatch({ type: 'viewAngle', deg: Number(e.target.value) })} />
      </div>

      <div className="btn-row">
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => dispatch({ type: 'center' })}>
          {t.center}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => dispatch({ type: 'fitHeight' })}>
          {t.fitHeight}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => dispatch({ type: 'fullWrap' })}>
          {t.fullWrap}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => dispatch({ type: 'resetPlacement' })}>
          {t.reset}
        </button>
      </div>

      <div className="field">
        <label htmlFor="scale">
          {t.width} <span className="mono muted">{round1(p.widthMm)} мм</span>
        </label>
        <input id="scale" type="range" min="4" max={Math.round(C)} step="0.5" value={Math.min(p.widthMm, C)} onChange={(e) => setNum('widthMm', e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="rot">
          {t.rotation} <span className="mono muted">{Math.round(p.rotationDeg)}°</span>
        </label>
        <input id="rot" type="range" min="-180" max="180" step="1" value={p.rotationDeg} onChange={(e) => setNum('rotationDeg', e.target.value)} />
      </div>

      <div className="num-grid">
        <NumField id="w-mm" label={t.width} unit="мм" value={p.widthMm} onCommit={(v) => setNum('widthMm', v)} />
        <NumField id="h-mm" label={t.height} unit="мм" value={p.heightMm} readOnly />
        <NumField id="u-mm" label={t.posU} unit="мм" value={p.uMm} onCommit={(v) => setNum('uMm', v)} />
        <NumField id="v-mm" label={t.posV} unit="мм" value={p.vMm} onCommit={(v) => setNum('vMm', v)} />
      </div>

      <dl className="specs specs--inline">
        <div>
          <dt>{t.dpi}</dt>
          <dd className={`dpi dpi--${der.dpiLevel}`}>{dpiText}</dd>
        </div>
        <div>
          <dt>{t.coverage}</dt>
          <dd>{der.coverageRatio != null ? `${Math.round(der.coverageRatio * 100)}%` : '—'}</dd>
        </div>
      </dl>

      <ul className="notices">
        {warn(s, der).map((w) => (
          <li key={w.text} className={`notice notice--${w.level}`}>
            {w.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NumField({ id, label, unit, value, onCommit, readOnly }: { id: string; label: string; unit: string; value: number; onCommit?: (v: string) => void; readOnly?: boolean }) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <div className="num">
      <label htmlFor={id}>{label}</label>
      <div className="num__box">
        <input
          id={id}
          inputMode="decimal"
          value={draft ?? String(round1(value))}
          readOnly={readOnly}
          onFocus={() => setDraft(String(round1(value)))}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft != null && onCommit) onCommit(draft);
            setDraft(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <span className="mono">{unit}</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ 5. print options
export function StepPrint({ s, dispatch }: StepProps) {
  const { d } = useI18n();
  const t = d.cfg.print;
  const der = derive(s);
  const rec = recommendedInk(s.material);
  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>

      <h3 className="step__sub">{t.inkTitle}</h3>
      <div className="choice-list" role="radiogroup" aria-label={t.inkTitle}>
        {INK_SETUPS.map((ink) => (
          <button key={ink} type="button" role="radio" aria-checked={s.ink === ink} className={`choice choice--row${s.ink === ink ? ' is-on' : ''}`} onClick={() => dispatch({ type: 'ink', ink })}>
            <InkLayers ink={ink} />
            <span className="choice__body">
              <span className="choice__name">
                {t.inks[ink].label}
                {ink === rec && <span className="pill">{t.recommendedFor}</span>}
              </span>
              <span className="choice__desc">{t.inks[ink].d}</span>
            </span>
          </button>
        ))}
      </div>

      <ul className="notices">
        {inkWarnings(s.material, s.ink, d.cfg.warnings).map((w) => (
            <li key={w.text} className={`notice notice--${w.level}`}>
              {w.text}
            </li>
          ))}
      </ul>

      <h3 className="step__sub">{t.coverageTitle}</h3>
      {der.suggestedCoverage && <p className="muted small">{fmt(t.suggested, { v: t.coverages[der.suggestedCoverage].label })}</p>}
      <div className="choice-grid choice-grid--3" role="radiogroup" aria-label={t.coverageTitle}>
        {COVERAGES.map((cv) => (
          <button key={cv} type="button" role="radio" aria-checked={s.coverage === cv} className={`choice choice--cov${s.coverage === cv ? ' is-on' : ''}`} onClick={() => dispatch({ type: 'coverage', coverage: cv })}>
            <CoverageIcon kind={cv} />
            <span className="choice__name">{t.coverages[cv].label}</span>
            <span className="choice__desc">{t.coverages[cv].d}</span>
          </button>
        ))}
      </div>

      {s.coverage === 'full_wrap' && s.artwork && der.wrapFraction < 0.85 && (
        <div className="notice notice--info notice--action">
          <span>{fmt(t.wrapHint, { p: Math.round(der.wrapFraction * 100) })}</span>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => dispatch({ type: 'fullWrap' })}>
            {t.applyWrap}
          </button>
        </div>
      )}
    </div>
  );
}

function InkLayers({ ink }: { ink: string }) {
  const layers = ink === 'cmyk' ? ['cmyk'] : ink === 'cmyk_white' ? ['white', 'cmyk'] : ['white', 'cmyk', 'varnish'];
  return (
    <span className="ink-stack" aria-hidden="true">
      {layers.map((l, i) => (
        <span key={l} className={`ink-stack__l ink-stack__l--${l}`} style={{ transform: `translateY(${-i * 8}px)` }} />
      ))}
      <span className="ink-stack__l ink-stack__l--glass" />
    </span>
  );
}

function CoverageIcon({ kind }: { kind: string }) {
  const w = kind === 'single_logo' ? 14 : kind === 'partial' ? 40 : 96;
  return (
    <svg viewBox="0 0 100 40" className="cov-icon" aria-hidden="true">
      <rect x="2" y="6" width="96" height="28" rx="2" className="cov-icon__band" />
      <rect x={50 - w / 2} y={kind === 'single_logo' ? 13 : 9} width={w} height={kind === 'single_logo' ? 14 : 22} rx="1.5" className="cov-icon__ink" />
    </svg>
  );
}

// ------------------------------------------------------------------ 6. quantity
export function StepQuantity({ s, dispatch }: StepProps) {
  const { d, nf } = useI18n();
  const t = d.cfg.quantity;
  const isPreset = (QUANTITY_PRESETS as readonly number[]).includes(s.quantity);
  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <p className="step__lead">{t.lead}</p>
      <div className="qty-grid" role="radiogroup" aria-label={t.title}>
        {QUANTITY_PRESETS.map((q) => (
          <button key={q} type="button" role="radio" aria-checked={s.quantity === q} className={`qty${s.quantity === q ? ' is-on' : ''}`} onClick={() => dispatch({ type: 'quantity', quantity: q })}>
            <span className="qty__n">{nf(q)}</span>
            <span className="qty__u">{d.common.pcs}</span>
          </button>
        ))}
      </div>
      <div className="field field--row">
        <label htmlFor="qty-custom">{t.custom}</label>
        <div className="num__box num__box--wide">
          <input
            id="qty-custom"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={isPreset ? '' : s.quantity || ''}
            placeholder={nf(s.quantity)}
            onChange={(e) => dispatch({ type: 'quantity', quantity: Math.max(0, Math.round(Number(e.target.value))) })}
          />
          <span className="mono">{d.common.pcs}</span>
        </div>
      </div>
      {s.quantity > 0 && s.quantity < 100 && <p className="notice notice--info">{t.smallRun}</p>}
      <div className="num-grid">
        <div className="num">
          <label htmlFor="sku">{t.skus}</label>
          <div className="num__box">
            <input id="sku" type="number" min={1} max={500} value={s.skuCount} onChange={(e) => dispatch({ type: 'skuCount', skuCount: Math.max(1, Math.round(Number(e.target.value)) || 1) })} />
          </div>
          <span className="hint">{t.skusHint}</span>
        </div>
        <div className="num">
          <label htmlFor="date">
            {t.date} <span className="muted">({d.common.optional})</span>
          </label>
          <div className="num__box">
            <input id="date" type="date" value={s.targetDate} onChange={(e) => dispatch({ type: 'targetDate', date: e.target.value })} />
          </div>
        </div>
      </div>
    </div>
  );
}
